import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Scale } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/contexts/AuthContext";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", agency_name: "", website: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await register(form);
      nav("/onboarding");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-2 bg-white">
      <div className="hidden md:block relative bg-[#0B1B3D]">
        <div className="absolute inset-0" style={{
          backgroundImage: "url(https://images.pexels.com/photos/8112112/pexels-photo-8112112.jpeg)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          opacity: 0.28,
        }} />
        <div className="relative z-10 p-12 flex flex-col h-full text-white">
          <Link to="/" className="flex items-center gap-2">
            <Scale className="w-5 h-5" strokeWidth={1.75} />
            <span className="font-serif text-xl font-medium">FOIA Assist</span>
          </Link>
          <div className="mt-auto space-y-6">
            <div className="border border-white/20 p-6">
              <div className="font-mono text-xs uppercase tracking-widest text-[#C5A059] mb-2">45-Day Trial</div>
              <p className="font-serif text-2xl leading-snug">No credit card. Full feature access. A sample case is pre-loaded so your team can start in 15 minutes.</p>
            </div>
            <ul className="space-y-2 text-sm text-white/80">
              <li>— AI-powered exemption flagging</li>
              <li>— Immutable audit trail</li>
              <li>— Real-time compliance dashboard</li>
              <li>— Stripe or NET-30 PO/Invoice billing</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 md:p-16">
        <div className="w-full max-w-md">
          <div className="font-mono text-xs uppercase tracking-widest text-slate-500 mb-3">Activate Trial</div>
          <h1 className="font-serif text-4xl tracking-tight font-medium mb-8">Create your agency workspace.</h1>

          <form onSubmit={submit} className="space-y-4">
            {/* Honeypot: hidden bot trap — legitimate humans never see or fill this */}
            <input
              type="text"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              value={form.website}
              onChange={(e) => setForm({ ...form, website: e.target.value })}
              className="hidden"
              aria-hidden="true"
              data-testid="register-honeypot"
            />
            <div>
              <label className="fa-label">Your Name</label>
              <input data-testid="register-name-input" className="fa-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <label className="fa-label">Agency / Department</label>
              <input data-testid="register-agency-input" className="fa-input" required placeholder="City of Springfield — Clerk's Office" value={form.agency_name} onChange={(e) => setForm({ ...form, agency_name: e.target.value })} />
            </div>
            <div>
              <label className="fa-label">Work Email</label>
              <input data-testid="register-email-input" className="fa-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div>
              <label className="fa-label">Password</label>
              <input data-testid="register-password-input" className="fa-input" type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
            </div>
            {error && <div className="fa-card p-3 border-red-200 bg-red-50 text-red-700 text-sm" data-testid="register-error">{error}</div>}
            <button data-testid="register-submit-button" type="submit" disabled={busy} className="fa-btn-primary w-full justify-center mt-2">
              {busy ? "Creating workspace…" : "Activate 45-day trial"}
            </button>
          </form>

          <div className="flex items-center gap-4 my-6">
            <div className="h-px bg-slate-200 flex-1" />
            <span className="text-xs uppercase tracking-widest text-slate-400">or</span>
            <div className="h-px bg-slate-200 flex-1" />
          </div>

          <button data-testid="register-google-button" onClick={googleLogin} className="fa-btn-secondary w-full justify-center">
            <svg width="16" height="16" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5h-1.9V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.5 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.5-5.2l-6.2-5.2C29.3 35 26.8 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.6 39.5 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.2-4.1 5.5l6.2 5.2c-.4.4 6.6-4.8 6.6-14.7 0-1.2-.1-2.4-.4-3.5z"/></svg>
            Continue with Google
          </button>

          <p className="text-sm text-slate-600 mt-8">
            Already activated?{" "}
            <Link to="/login" className="text-[#0B1B3D] underline decoration-[#C5A059] decoration-2 underline-offset-4" data-testid="register-to-login-link">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
